/* ==========================================================================
   Shoppable reels - behaviour
   Loaded from /assets so the browser caches it across page views instead of
   re-parsing ~78KB of inline script inside every HTML document.
   Section-agnostic: it boots every [data-sr-reels-section] on the page and 
   re-boots on shopify:section:load for the theme editor.
   ========================================================================== */
(() => {
  'use strict';

  const registry = window.SrReelsRegistry || (window.SrReelsRegistry = {
    controllers: new Map(),
    listenersReady: false,
    lockCount: 0,
    bodyState: null,
    activeController: null
  });

  const icons = {
    muted: '<svg aria-hidden="true" viewBox="0 0 24 24" fill="none"><path d="M5 9v6h4l5 4V5L9 9H5Z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/><path d="m18 9 4 4m0-4-4 4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>',
    sound: '<svg aria-hidden="true" viewBox="0 0 24 24" fill="none"><path d="M5 9v6h4l5 4V5L9 9H5Z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/><path d="M17 9.5c.8.7 1.2 1.5 1.2 2.5S17.8 13.8 17 14.5M19.5 7c1.5 1.3 2.3 3 2.3 5s-.8 3.7-2.3 5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>'
  };

  const escapeHtml = (value) => String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const safeUrl = (value, sameOrigin = false) => {
    if (typeof value !== 'string' || !value.trim()) return '';
    try {
      const parsed = new URL(value, window.location.origin);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return '';
      if (sameOrigin && parsed.origin !== window.location.origin) return '';
      return parsed.href;
    } catch (error) {
      return '';
    }
  };

  /* ----------------------------------------------------------------
     Image helpers.
     Every url is generated by Liquid (the same filter usage the original
     file used); nothing is derived or rewritten in the browser.
       u = 700w  default src
       s = srcset "400w, 700w, 1100w"
       t = 150w  thumbnail / low-quality placeholder
  ---------------------------------------------------------------- */
  /* Measured from the real layout: the product panel is 54% of the modal
     (max 1200px) minus clamp(22px,2.4vw,36px) of padding each side ~= 580px.
     The multi-product grid is 2 columns inside that ~= 280px per card.
     Declaring the true box stops the browser reaching for a 1200px file to
     fill a 280px slot. */
  const SR_MAIN_SIZES = '(max-width: 749px) 70vw, 420px';
  const SR_CARD_SIZES = '(max-width: 749px) 44vw, 280px';

  const imgMain = (image) => safeUrl(image?.u);
  const imgThumb = (image) => safeUrl(image?.t) || safeUrl(image?.u);
  const imgSet = (image) => (typeof image?.s === 'string' ? image.s : '');

  /* Identity for "is this the same picture?" comparisons. */
  const imgKey = (image) => safeUrl(image?.u) || '';

  /* Variants store an index into product.images when the variant picture is
     already in the gallery, which keeps many-variant products tiny. */
  const resolveImage = (product, ref) => {
    if (ref == null) return null;
    if (typeof ref === 'number') return (Array.isArray(product?.images) ? product.images[ref] : null) || null;
    return ref;
  };

  /* The picture the modal will actually show first. Mirrors the resolution
     order used by renderSingleProduct so warming can never target the wrong
     image (e.g. warming product.featured while a variant image is shown). */
  const displayImageFor = (product) => {
    if (!product) return null;
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const variant = variants.find((item) => item.available) || variants[0] || null;
    return resolveImage(product, variant?.img)
      || product.featuredImage
      || (Array.isArray(product.images) ? product.images[0] : null)
      || null;
  };

  const shopRouteRoot = () => {
    const configured = window.Shopify?.routes?.root;
    const safe = safeUrl(typeof configured === 'string' ? configured : '/', true);
    if (!safe) return '/';
    const path = new URL(safe).pathname;
    return path.endsWith('/') ? path : `${path}/`;
  };

  const lockPage = () => {
    registry.lockCount += 1;
    if (registry.lockCount !== 1) return;
    const body = document.body;
    const html = document.documentElement;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const scrollbar = Math.max(0, window.innerWidth - html.clientWidth);
    registry.bodyState = {
      scrollY,
      body: {
        overflow: body.style.overflow,
        position: body.style.position,
        top: body.style.top,
        left: body.style.left,
        right: body.style.right,
        width: body.style.width,
        paddingRight: body.style.paddingRight
      },
      htmlOverflow: html.style.overflow
    };
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    if (scrollbar) body.style.paddingRight = `${scrollbar}px`;
  };

  const unlockPage = () => {
    registry.lockCount = Math.max(0, registry.lockCount - 1);
    if (registry.lockCount || !registry.bodyState) return;
    const state = registry.bodyState;
    const body = document.body;
    const html = document.documentElement;
    body.style.overflow = state.body.overflow;
    body.style.position = state.body.position;
    body.style.top = state.body.top;
    body.style.left = state.body.left;
    body.style.right = state.body.right;
    body.style.width = state.body.width;
    body.style.paddingRight = state.body.paddingRight;
    html.style.overflow = state.htmlOverflow;
    registry.bodyState = null;
    window.scrollTo(0, state.scrollY);
  };

  class SrReelsController {
    constructor(root) {
      this.root = root;
      this.id = root.dataset.sectionId;
      this.abort = new AbortController();
      this.track = root.querySelector('.sr-reels-track');
      this.cards = Array.from(root.querySelectorAll('[data-sr-reels-card]'));
      this.videos = this.cards.map((card) => card.querySelector('.sr-reels-preview-video')).filter(Boolean);
      this.visibleVideos = new Set();
      this.modal = root.querySelector('[data-sr-reels-modal]');
      this.dialog = root.querySelector('.sr-reels-dialog');
      this.videoColumn = root.querySelector('.sr-reels-modal-video-column');
      this.panel = root.querySelector('[data-sr-reels-panel]');
      this.videoHost = root.querySelector('.sr-reels-modal-video-host');
      this.videoColumn = root.querySelector('.sr-reels-modal-video-column');
      this.mobileStack = root.querySelector('[data-sr-reels-mobile-stack]');
      this.progressFill = root.querySelector('[data-sr-reels-progress-fill]');
      this.progressTrack = root.querySelector('[data-sr-reels-progress-track]');
      this.scrubbing = false;
      this.closeButton = root.querySelector('[data-sr-reels-close]');
      this.unmuteButton = root.querySelector('[data-sr-reels-unmute]');
      this.muteButton = root.querySelector('[data-sr-reels-mute]');
      this.shareButton = root.querySelector('[data-sr-reels-share]');
      this.shareStatus = root.querySelector('.sr-reels-share-status');
      this.prev = root.querySelector('.sr-reels-prev');
      this.next = root.querySelector('.sr-reels-next');
      this.modalPrev = root.querySelector('[data-sr-reels-modal-prev]');
      this.modalNext = root.querySelector('[data-sr-reels-modal-next]');
      this.swipeHint = root.querySelector('[data-sr-reels-swipe-hint]');
      this.modalVideo = null;
      this.activeCard = null;
      this.reelData = null;
      this.singleProduct = null;
      this.isOpen = false;
      this.mobileMode = false;
      this.mobileSlides = [];
      this.mobileVideos = [];
      this.mobileActiveIndex = -1;
      this.mobileOpeningIndex = -1;
      this.mobileScrollFrame = 0;
      this.mobileScrollTimer = 0;
      this.mobilePreloadTimer = 0;
      this.mobileActivationToken = 0;
      this.mobileObserver = null;
      this.mobileRatios = new Map();
      this.mobilePlaybackAbort = null;
      this.scrollFrame = 0;
      this.closeToken = 0;
      this.modalAbort = null;
      this.progressAbort = null;
      this.modalVideoEl = null;
      this.isNavigating = false;
      this.switchTimer = 0;
      this.prefetchTimer = 0;
      this.progressFrame = 0;
      this.progressVideoFrame = 0;
      this.warmedImageUrls = new Set();
      this.pendingPlay = new Set();
      this.releaseTimers = new WeakMap();
      this.galleryWarmQueue = [];
      this.galleryWarmTimer = 0;
      this.gestureUnlockArmed = false;
      this.warmedImages = new Map();
      this.imageWarmObserver = null;
      this.mainImageReady = null;
      this.activeProduct = null;
      this.carouselTrack = null;
      this.carouselSegs = [];
      this.carouselFrame = 0;
      this.hasUnmuted = false;
      this.userMuted = true;
      this.config = this.readJson(root.querySelector('.sr-reels-config')) || {};
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.bind();
      this.observeVideos();
      this.observeProductImages();
      this.updateNavigation();
    }

    /* Parsed reel payloads are cached on the data node. Opening a modal used
       to run JSON.parse three times on the same (large) blob on the click
       path; buildMobileStack ran it once per reel. Now it runs once, ever. */
    readJson(node) {
      if (!node) return null;
      if (node.__srParsed !== undefined) return node.__srParsed;
      const source = node.tagName === 'TEMPLATE' ? node.content.textContent : node.textContent;
      let parsed = null;
      try { parsed = JSON.parse(source); } catch (error) { parsed = null; }
      node.__srParsed = parsed;
      return parsed;
    }

    /* Safari refuses the very first play() on a fresh document more often than
       Chrome does, and the old code swallowed that rejection with no retry.
       Because the IntersectionObserver had already fired and the video stayed
       intersecting, nothing ever asked again -- so previews sat frozen until
       something else (opening a modal, which IS a user gesture) unlocked
       autoplay for the document. Now every rejection is retried: once the
       media actually has data, and again on the first interaction anywhere. */
    safePlay(video) {
      if (!video || registry.activeController || document.hidden) return;
      /* defaultMuted keeps the muted ATTRIBUTE in sync. Safari re-reads the
         attribute when the source loads; setting only the property can leave
         it treated as an unmuted video and therefore blocked. */
      video.muted = true;
      video.defaultMuted = true;
      if (!video.hasAttribute('muted')) video.setAttribute('muted', '');
      if (!video.hasAttribute('playsinline')) video.setAttribute('playsinline', '');
      if (video.preload === 'none') {
        video.preload = 'auto';
        video.setAttribute('preload', 'auto');
      }

      const attempt = () => {
        if (registry.activeController || document.hidden || !video.isConnected) return;
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.then(() => this.pendingPlay.delete(video), () => this.queuePlayRetry(video));
        }
      };

      /* readyState < HAVE_CURRENT_DATA: asking Safari to play now tends to be
         rejected outright, so also wait for data and try again. */
      if (video.readyState < 2) {
        video.addEventListener('loadeddata', attempt, { once: true });
        video.addEventListener('canplay', attempt, { once: true });
      }
      attempt();
    }

    queuePlayRetry(video) {
      if (!video?.isConnected) return;
      this.pendingPlay.add(video);
      this.armGestureUnlock();
    }

    /* Any user gesture grants the document autoplay permission in Safari.
       Piggyback on the first one to flush anything still frozen. */
    armGestureUnlock() {
      if (this.gestureUnlockArmed) return;
      this.gestureUnlockArmed = true;
      const unlock = () => {
        this.gestureUnlockArmed = false;
        const pending = Array.from(this.pendingPlay);
        this.pendingPlay.clear();
        if (registry.activeController || document.hidden) return;
        pending.forEach((video) => {
          if (!video.isConnected || !this.visibleVideos.has(video)) return;
          video.muted = true;
          const playPromise = video.play();
          if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
        });
      };
      ['pointerdown', 'touchstart', 'keydown', 'scroll'].forEach((type) => {
        document.addEventListener(type, unlock, { once: true, passive: true, capture: true });
      });
    }

    /* pause() stops playback but the browser carries on downloading while
       preload is "auto". Scrolling quickly past a row of reels therefore left
       every video it touched buffering indefinitely, saturating the connection
       to the CDN -- which is why the last reel's video and the modal's second
       carousel image could sit stalled for a minute behind them.
       Setting preload="none" and calling load() aborts the in-flight fetch.
       Debounced so a quick scroll back and forth doesn't thrash the network. */
    releaseVideo(video, immediate = false) {
      if (!video) return;
      const existing = this.releaseTimers.get(video);
      if (existing) window.clearTimeout(existing);
      const run = () => {
        this.releaseTimers.delete(video);
        if (this.visibleVideos.has(video) || video === this.modalVideo) return;
        if (video.preload === 'none' && video.networkState !== 2) return;
        video.pause();
        /* autoplay is only wanted for the very first paint; keeping it would
           let load() restart an off-screen video. */
        video.removeAttribute('autoplay');
        video.preload = 'none';
        video.setAttribute('preload', 'none');
        try { video.load(); } catch (error) {}
      };
      if (immediate) run();
      else this.releaseTimers.set(video, window.setTimeout(run, 500));
    }

    /* When a modal opens, hand the entire connection to the modal video and the
       product images rather than letting background previews keep buffering. */
    pausePreviews() {
      this.videos.forEach((video) => {
        video.pause();
        if (video !== this.modalVideo) this.releaseVideo(video, true);
      });
    }

    resumePreviews() {
      if (registry.activeController || document.hidden) return;
      this.visibleVideos.forEach((video) => this.safePlay(video));
    }

    observeVideos() {
      if (!this.track) return;
      if (!('IntersectionObserver' in window)) {
        this.videos.forEach((video) => { this.visibleVideos.add(video); this.safePlay(video); });
        return;
      }
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (this.isOpen && video === this.modalVideo) return;
          if (entry.isIntersecting) {
            this.visibleVideos.add(video);
            this.safePlay(video);
          } else {
            this.visibleVideos.delete(video);
            video.pause();
            this.releaseVideo(video);
          }
        });
      }, { root: this.track, rootMargin: '0px 25%', threshold: 0.01 });
      this.videos.forEach((video) => this.observer.observe(video));
    }

    /* Warm the product image for every reel card as it approaches the visible
       rail. This starts the Shopify CDN request before the shopper opens the
       modal, without downloading galleries for off-screen reels. Hover,
       keyboard focus and touch remain as an immediate fallback. */
    observeProductImages() {
      if (!this.track || !this.cards.length) return;
      const connection = navigator.connection || navigator.webkitConnection;
      if (connection?.saveData || connection?.effectiveType === 'slow-2g' || connection?.effectiveType === '2g') return;

      const warm = (card) => this.warmCardImages(card, false);
      if (!('IntersectionObserver' in window)) {
        this.cards.slice(0, 4).forEach(warm);
        return;
      }

      this.imageWarmObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          warm(entry.target);
          observer.unobserve(entry.target);
        });
      }, { root: this.track, rootMargin: '0px 75%', threshold: 0.01 });
      this.cards.forEach((card) => this.imageWarmObserver.observe(card));
    }

    bind() {
      const signal = this.abort.signal;
      this.cards.forEach((card) => {
        card.addEventListener('click', () => this.open(card), { signal });
        /* Hover/focus warm: the first product's main image and its thumbnail
           strip only. Enough to make the click feel instant without pulling
           a whole gallery for a reel that may never be opened. */
        const warm = () => this.warmCardImages(card, true);
        card.addEventListener('pointerenter', warm, { signal, once: true, passive: true });
        card.addEventListener('focusin', warm, { signal, once: true });
        card.addEventListener('touchstart', warm, { signal, once: true, passive: true });
      });
      this.closeButton?.addEventListener('click', () => this.close(), { signal });
      /* stopPropagation, not just a selector guard on the parent. These sit
         inside the video column, whose click handler toggles playback -- so
         without this a mute click also paused the video, which made it look
         like mute "did nothing" (the audio was off because playback stopped,
         not because the video was muted). */
      this.unmuteButton?.addEventListener('click', (event) => {
        event.stopPropagation();
        this.setMuted(false);
      }, { signal });
      this.muteButton?.addEventListener('click', (event) => {
        event.stopPropagation();
        this.setMuted(!this.userMuted);
      }, { signal });
      this.shareButton?.addEventListener('click', (event) => {
        event.stopPropagation();
        this.share();
      }, { signal });
      this.bindScrubbing(signal);
      ['play', 'pause', 'ended'].forEach((type) => {
        this.videoHost?.addEventListener(type, () => this.syncPausedState(), { capture: true, signal });
      });
      /* Click the video itself to toggle playback. Anything interactive --
         the control cluster, the progress bar, nav arrows -- is excluded by
         container, not just by tag name, so a click on an <svg> inside a
         control can never fall through to here. */
      this.videoColumn?.addEventListener('click', (event) => {
        if (event.target.closest(
          '.sr-reels-modal-controls, .sr-reels-progress-track, .sr-reels-mobile-stack, button, a, input, select, label, [role="slider"]'
        )) return;
        this.togglePlayback();
      }, { signal });
      this.modal?.addEventListener('click', (event) => {
        if (event.target === this.modal) this.close();
      }, { signal });
      document.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      document.addEventListener('visibilitychange', () => this.onVisibilityChange(), { signal });
      this.panel?.addEventListener('click', (event) => this.onPanelClick(event), { signal });
      this.panel?.addEventListener('change', (event) => this.onPanelChange(event), { signal });
      this.mobileStack?.addEventListener('click', (event) => this.onMobileStackClick(event), { signal });
      this.mobileStack?.addEventListener('scroll', () => this.onMobileScroll(), { signal, passive: true });
      this.prev?.addEventListener('click', () => this.scrollByPage(-1), { signal });
      this.next?.addEventListener('click', () => this.scrollByPage(1), { signal });
      this.modalPrev?.addEventListener('click', () => this.navigateModal(-1), { signal });
      this.modalNext?.addEventListener('click', () => this.navigateModal(1), { signal });
      this.track?.addEventListener('scroll', () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = 0;
          this.updateNavigation();
        });
      }, { signal, passive: true });
      window.addEventListener('resize', () => this.handleResize(), { signal, passive: true });
    }

    scrollByPage(direction) {
      if (!this.track) return;
      this.track.scrollBy({ left: direction * Math.max(200, this.track.clientWidth * .82), behavior: this.reducedMotion ? 'auto' : 'smooth' });
    }

    updateNavigation() {
      if (!this.track) return;
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max - 2;
    }

    updateModalNavigation() {
      const hasMultiple = this.cards.length > 1 && !this.mobileMode;
      if (this.modalPrev) {
        this.modalPrev.hidden = !hasMultiple;
        this.modalPrev.disabled = !hasMultiple;
      }
      if (this.modalNext) {
        this.modalNext.hidden = !hasMultiple;
        this.modalNext.disabled = !hasMultiple;
      }
    }

    openSheet() {
      // Kept as a harmless compatibility method for older markup.
    }

    closeSheet() {
      this.dialog?.classList.remove('sr-reels-sheet-open');
    }

    isMobileViewport() {
      return Boolean(this.mobileQuery?.matches);
    }

    handleResize() {
      this.updateNavigation();
      if (!this.isOpen) return;
      const shouldUseMobile = this.isMobileViewport();
      if (shouldUseMobile === this.mobileMode) return;
      const card = this.activeCard;
      this.close({ restoreFocus: false, immediate: true });
      if (card?.isConnected) requestAnimationFrame(() => this.open(card));
    }

    onMobileScroll() {
      if (!this.isOpen || !this.mobileMode || !this.mobileStack) return;
      if (Math.abs(this.mobileStack.scrollTop - (this.mobileSlides[this.mobileOpeningIndex]?.offsetTop || 0)) > 14) {
        this.hideSwipeHint(true);
      }

      if (!this.mobileScrollFrame) {
        this.mobileScrollFrame = requestAnimationFrame(() => {
          this.mobileScrollFrame = 0;
          if (!this.isOpen || !this.mobileMode || !this.mobileSlides.length) return;
          const top = this.mobileStack.scrollTop;
          let nearestIndex = 0;
          let nearestDistance = Number.POSITIVE_INFINITY;
          this.mobileSlides.forEach((slide, index) => {
            const distance = Math.abs(slide.offsetTop - top);
            if (distance < nearestDistance) {
              nearestDistance = distance;
              nearestIndex = index;
            }
          });
          this.prewarmMobileReel(nearestIndex);
        });
      }

      if (this.mobileScrollTimer) window.clearTimeout(this.mobileScrollTimer);
      this.mobileScrollTimer = window.setTimeout(() => {
        this.mobileScrollTimer = 0;
        if (!this.isOpen || !this.mobileMode || !this.mobileSlides.length) return;
        const top = this.mobileStack.scrollTop;
        let nearestIndex = 0;
        let nearestDistance = Number.POSITIVE_INFINITY;
        this.mobileSlides.forEach((slide, index) => {
          const distance = Math.abs(slide.offsetTop - top);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestIndex = index;
          }
        });
        this.activateMobileReel(nearestIndex);
      }, 140);
    }

    maybeShowSwipeHint() {
      if (!this.swipeHint || !this.mobileMode || this.cards.length < 2) return;
      let alreadySeen = false;
      try { alreadySeen = sessionStorage.getItem('srReelsSwipeHintSeen') === '1'; } catch (error) {}
      if (alreadySeen) return;
      window.setTimeout(() => {
        if (!this.isOpen || !this.mobileMode || this.mobileActiveIndex !== this.mobileOpeningIndex) return;
        this.swipeHint.classList.add('sr-reels-swipe-hint-visible');
      }, 420);
    }

    hideSwipeHint(markSeen = false) {
      this.swipeHint?.classList.remove('sr-reels-swipe-hint-visible');
      if (!markSeen) return;
      try { sessionStorage.setItem('srReelsSwipeHintSeen', '1'); } catch (error) {}
    }

    mobileProductMarkup(products, eagerImage = false) {
      if (!Array.isArray(products) || !products.length) return '';
      const action = this.actionConfig();
      const customLabel = this.reelData?.ctaLabel;
      const cards = products.map((product) => {
        const variants = Array.isArray(product.variants) ? product.variants : [];
        const variant = variants.find((item) => item.available) || variants[0] || null;
        const available = Boolean(variant?.available);
        const productUrl = safeUrl(product.url, true);
        const cardImage = displayImageFor(product) || product.featuredImage;
        const imageUrl = imgThumb(cardImage);
        const imageMarkup = imageUrl
          ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(cardImage?.alt || product.title || '')}" width="108" height="108" loading="${eagerImage ? 'eager' : 'lazy'}" fetchpriority="${eagerImage ? 'high' : 'low'}" decoding="async">`
          : '<span class="sr-reels-mobile-product-image-placeholder">No image</span>';
        const image = productUrl
          ? `<a class="sr-reels-mobile-product-image" href="${escapeHtml(productUrl)}" aria-label="View ${escapeHtml(product.title)}">${imageMarkup}</a>`
          : `<span class="sr-reels-mobile-product-image">${imageMarkup}</span>`;
        const title = productUrl
          ? `<a class="sr-reels-mobile-product-title" href="${escapeHtml(productUrl)}">${escapeHtml(product.title)}</a>`
          : `<span class="sr-reels-mobile-product-title">${escapeHtml(product.title)}</span>`;
        const currentPrice = variant?.price ?? product.price;
        const compareAt = variant?.compareAtPrice ?? product.compareAtPrice;
        const price = `<div class="sr-reels-mobile-product-price"><strong>${escapeHtml(this.money(currentPrice))}</strong>${Number(compareAt) > Number(currentPrice) ? `<s>${escapeHtml(this.money(compareAt))}</s>` : ''}</div>`;
        let actionMarkup = '';
        if (action.quick) {
          actionMarkup = `<button class="sr-reels-mobile-product-action" type="button" data-sr-reels-mobile-add data-variant-id="${variant?.id || ''}" ${available ? '' : 'disabled'}><span>${escapeHtml(available ? (customLabel || 'Add to cart') : 'Sold out')}</span><svg aria-hidden="true" viewBox="0 0 24 24" fill="none"><path d="M5 12h14m-6-6 6 6-6 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></button>`;
        } else if (productUrl) {
          actionMarkup = `<a class="sr-reels-mobile-product-action" href="${escapeHtml(productUrl)}"><span>${escapeHtml(customLabel || action.label)}</span><svg aria-hidden="true" viewBox="0 0 24 24" fill="none"><path d="M5 12h14m-6-6 6 6-6 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></a>`;
        }
        return `<article class="sr-reels-mobile-product-card">
          <div class="sr-reels-mobile-product-main">
            ${image}
            <div class="sr-reels-mobile-product-copy">${title}${price}</div>
          </div>
          ${actionMarkup}
          <div class="sr-reels-mobile-feedback" role="status" aria-live="polite"></div>
        </article>`;
      }).join('');
      return `<div class="sr-reels-mobile-products${products.length === 1 ? ' is-single' : ''}" aria-label="Products in this reel">${cards}</div>`;
    }

    buildMobileStack(openIndex) {
      if (!this.mobileStack || !this.cards.length) return false;
      this.clearMobileStack();
      const safeIndex = Math.min(this.cards.length - 1, Math.max(0, openIndex));
      const fragment = document.createDocumentFragment();

      this.cards.forEach((card, index) => {
        const data = this.readJson(document.getElementById(card.dataset.dataId));
        const preview = this.cardVideo(card);
        if (!data || !preview) return;

        const slide = document.createElement('article');
        slide.className = 'sr-reels-mobile-reel';
        slide.dataset.reelIndex = String(index);
        slide.setAttribute('aria-label', data.label || `Reel ${index + 1}`);

        const video = preview.cloneNode(true);
        const source = safeUrl(data.mobileVideoUrl) || safeUrl(preview.getAttribute('src')) || safeUrl(preview.currentSrc);
        video.className = 'sr-reels-mobile-video';
        video.removeAttribute('aria-hidden');
        video.removeAttribute('tabindex');
        video.removeAttribute('src');
        video.querySelectorAll('source').forEach((sourceNode) => sourceNode.removeAttribute('src'));
        if (source) video.dataset.srSource = source;
        video.muted = this.userMuted;
        video.defaultMuted = this.userMuted;
        video.loop = Boolean(this.config.modalLoop);
        video.controls = false;
        video.removeAttribute('controls');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.setAttribute('disablepictureinpicture', '');
        video.setAttribute('controlslist', 'nodownload noplaybackrate noremoteplayback');
        video.preload = 'none';
        video.setAttribute('preload', 'none');

        slide.appendChild(video);
        this.reelData = data;
        slide.insertAdjacentHTML(
          'beforeend',
          this.mobileProductMarkup(
            Array.isArray(data.products) ? data.products.filter(Boolean) : [],
            index === safeIndex
          )
        );
        fragment.appendChild(slide);
      });

      this.mobileStack.appendChild(fragment);
      this.mobileSlides = Array.from(this.mobileStack.querySelectorAll('.sr-reels-mobile-reel'));
      this.mobileVideos = this.mobileSlides.map((slide) => slide.querySelector('.sr-reels-mobile-video')).filter(Boolean);
      if (!this.mobileSlides.length) return false;

      this.mobileOpeningIndex = Math.min(this.mobileSlides.length - 1, safeIndex);
      this.mobileActiveIndex = -1;
      this.setupMobileObserver();
      this.mobileStack.scrollTop = this.mobileSlides[this.mobileOpeningIndex].offsetTop;
      this.prewarmMobileReel(this.mobileOpeningIndex);
      this.activateMobileReel(this.mobileOpeningIndex, true);

      requestAnimationFrame(() => {
        if (!this.isOpen || !this.mobileMode) return;
        this.mobileStack.scrollTop = this.mobileSlides[this.mobileOpeningIndex]?.offsetTop || 0;
      });
      return true;
    }

    attachMobileVideo(index, preload = 'metadata') {
      const video = this.mobileVideos[index];
      if (!video) return null;
      const source = safeUrl(video.dataset.srSource);
      if (!source) return video;

      video.preload = preload;
      video.setAttribute('preload', preload);
      if (video.getAttribute('src') !== source) {
        video.src = source;
        video.dataset.srAttached = '1';
        try { video.load(); } catch (error) {}
      } else if (video.readyState === 0) {
        try { video.load(); } catch (error) {}
      }
      return video;
    }

    releaseMobileVideo(index) {
      const video = this.mobileVideos[index];
      if (!video || index === this.mobileActiveIndex) return;
      video.pause();
      if (!video.hasAttribute('src')) return;
      video.removeAttribute('src');
      delete video.dataset.srAttached;
      delete video.dataset.srReelStarted;
      try { video.load(); } catch (error) {}
      this.mobileSlides[index]?.classList.remove('is-buffering');
    }

    prewarmMobileReel(index) {
      if (!Number.isInteger(index) || index < 0 || index >= this.mobileVideos.length) return;
      this.attachMobileVideo(index, index === this.mobileActiveIndex ? 'auto' : 'metadata');
      if (index + 1 < this.mobileVideos.length) this.attachMobileVideo(index + 1, 'auto');
    }

    setupMobileObserver() {
      this.mobileObserver?.disconnect();
      this.mobileRatios.clear();
      if (!this.mobileStack || !('IntersectionObserver' in window)) return;
      this.mobileObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const index = Number(entry.target.dataset.reelIndex);
          if (!Number.isInteger(index)) return;
          this.mobileRatios.set(index, entry.intersectionRatio);
          if (entry.isIntersecting && entry.intersectionRatio >= .12) this.prewarmMobileReel(index);
        });

        let bestIndex = -1;
        let bestRatio = 0;
        this.mobileRatios.forEach((ratio, index) => {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestIndex = index;
          }
        });
        if (bestIndex >= 0 && bestRatio >= .82) this.activateMobileReel(bestIndex);
      }, {
        root: this.mobileStack,
        rootMargin: '35% 0px 35% 0px',
        threshold: [0, .12, .35, .5, .68, .82, 1]
      });
      this.mobileSlides.forEach((slide) => this.mobileObserver.observe(slide));
    }

    activateMobileReel(index, initial = false) {
      if (!this.mobileMode || index < 0 || index >= this.mobileSlides.length) return;
      if (index === this.mobileActiveIndex && !initial) return;

      const previousIndex = this.mobileActiveIndex;
      const activationToken = ++this.mobileActivationToken;
      this.mobileActiveIndex = index;
      this.mobilePlaybackAbort?.abort();
      this.mobilePlaybackAbort = new AbortController();
      const signal = this.mobilePlaybackAbort.signal;

      this.mobileSlides.forEach((slide, slideIndex) => {
        slide.classList.toggle('is-active', slideIndex === index);
        if (slideIndex !== index) slide.classList.remove('is-buffering');
      });
      this.mobileVideos.forEach((video, videoIndex) => {
        if (videoIndex !== index) video.pause();
      });

      const slide = this.mobileSlides[index];
      const video = this.attachMobileVideo(index, 'auto');
      const card = this.cards[index];
      const data = this.readJson(document.getElementById(card?.dataset.dataId));
      if (!slide || !video || !card || !data) return;

      this.activeCard = card;
      this.reelData = data;
      this.modalVideo = video;
      video.muted = this.userMuted;
      video.defaultMuted = this.userMuted;
      video.loop = Boolean(this.config.modalLoop);

      if (!video.dataset.srReelStarted) {
        video.dataset.srReelStarted = '1';
        try { video.currentTime = 0; } catch (error) {}
      }

      const isCurrent = () => (
        this.isOpen &&
        this.mobileMode &&
        this.mobileActiveIndex === index &&
        this.modalVideo === video &&
        this.mobileActivationToken === activationToken
      );
      const showBuffering = () => {
        if (isCurrent() && video.readyState < 3) slide.classList.add('is-buffering');
      };
      const hideBuffering = () => slide.classList.remove('is-buffering');
      const attemptPlay = () => {
        if (!isCurrent()) return;
        const playPromise = video.play();
        if (playPromise?.then) {
          playPromise.then(() => {
            if (!isCurrent()) return;
            hideBuffering();
            this.runProgressLoop(video);
            this.warmMobileAdjacent(index);
          }).catch(() => {
            if (!isCurrent()) return;
            showBuffering();
          });
        }
      };

      video.addEventListener('waiting', showBuffering, { signal });
      video.addEventListener('stalled', showBuffering, { signal });
      video.addEventListener('loadstart', showBuffering, { signal });
      video.addEventListener('loadeddata', hideBuffering, { signal });
      video.addEventListener('playing', hideBuffering, { signal });
      video.addEventListener('canplay', () => {
        hideBuffering();
        if (isCurrent() && video.paused) attemptPlay();
      }, { signal });

      this.bindProgress(video);
      this.shareButton.hidden = !(this.config.showShare && data.showShare);
      this.dialog.setAttribute('aria-label', data.label || 'Shoppable reel');
      this.updateMuteButton();

      if (video.readyState >= 2) attemptPlay();
      else {
        showBuffering();
        try { video.load(); } catch (error) {}
      }

      this.warmMobileAdjacent(index);
      if (previousIndex >= 0 && previousIndex !== index) this.hideSwipeHint(true);
    }

    warmMobileAdjacent(index) {
      if (this.mobilePreloadTimer) window.clearTimeout(this.mobilePreloadTimer);
      this.mobilePreloadTimer = 0;

      const keep = new Set([index]);
      const previousIndex = index - 1;
      const nextIndex = index + 1;

      if (previousIndex >= 0) {
        keep.add(previousIndex);
        this.attachMobileVideo(previousIndex, 'metadata');
      }
      if (nextIndex < this.mobileVideos.length) {
        keep.add(nextIndex);
        this.attachMobileVideo(nextIndex, 'auto');
      }

      this.mobileVideos.forEach((video, videoIndex) => {
        if (!keep.has(videoIndex)) this.releaseMobileVideo(videoIndex);
      });

      this.mobilePreloadTimer = window.setTimeout(() => {
        this.mobilePreloadTimer = 0;
        if (!this.isOpen || !this.mobileMode || this.mobileActiveIndex !== index) return;
        const followingIndex = index + 2;
        if (followingIndex < this.mobileVideos.length) this.attachMobileVideo(followingIndex, 'metadata');
      }, 900);
    }

    clearMobileStack() {
      if (this.mobileScrollFrame) cancelAnimationFrame(this.mobileScrollFrame);
      this.mobileScrollFrame = 0;
      if (this.mobileScrollTimer) window.clearTimeout(this.mobileScrollTimer);
      this.mobileScrollTimer = 0;
      if (this.mobilePreloadTimer) window.clearTimeout(this.mobilePreloadTimer);
      this.mobilePreloadTimer = 0;
      this.mobileActivationToken += 1;
      this.mobileObserver?.disconnect();
      this.mobileObserver = null;
      this.mobileRatios.clear();
      this.mobilePlaybackAbort?.abort();
      this.mobilePlaybackAbort = null;
      this.mobileVideos.forEach((video) => {
        video.pause();
        video.removeAttribute('src');
        try { video.load(); } catch (error) {}
      });
      this.mobileSlides = [];
      this.mobileVideos = [];
      this.mobileActiveIndex = -1;
      this.mobileOpeningIndex = -1;
      this.mobileStack?.replaceChildren();
    }

    onMobileStackClick(event) {
      const addButton = event.target.closest('[data-sr-reels-mobile-add]');
      if (addButton) {
        event.preventDefault();
        event.stopPropagation();
        this.addToCart(addButton.dataset.variantId, 1, addButton);
      }
      // Video taps intentionally do nothing. Playback should remain continuous;
      // mute/unmute is controlled only by the dedicated sound control.
    }

    onTouchStart(event) {
      if (!this.isOpen || event.touches.length !== 1) return;
      const touch = event.touches[0];
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
      this.touchStartTime = Date.now();
    }

    onTouchEnd(event) {
      if (!this.isOpen || this.touchStartY == null) return;
      const startX = this.touchStartX;
      const startY = this.touchStartY;
      const elapsed = Date.now() - this.touchStartTime;
      this.touchStartX = null;
      this.touchStartY = null;
      // Ignore the gesture while the product sheet is open — that swipe belongs
      // to the sheet (scrolling or dismissing it), not to reel navigation.
      if (this.dialog?.classList.contains('sr-reels-sheet-open')) return;
      const touch = event.changedTouches[0];
      if (!touch) return;
      const deltaX = touch.clientX - startX;
      const deltaY = touch.clientY - startY;
      if (elapsed > 900) return;
      if (Math.abs(deltaY) < 60 || Math.abs(deltaY) < Math.abs(deltaX) * 1.4) return;
      this.navigateModal(deltaY < 0 ? 1 : -1);
    }

    navigateModal(direction) {
      if (!this.isOpen || this.mobileMode || this.isNavigating || this.cards.length < 2) return;
      const index = this.cards.indexOf(this.activeCard);
      const targetIndex = (index + direction + this.cards.length) % this.cards.length;
      const target = this.cards[targetIndex];
      if (!target) return;
      if (this.reducedMotion) {
        this.warmCardImages(target, true);
        this.mountReel(target);
        return;
      }
      this.isNavigating = true;
      /* mountReel (and therefore the product markup and its image request) used
         to wait for the 170ms fade-out to finish. Warm the target reel's images
         right now so that time is spent downloading instead of idling; the
         animation timing below is unchanged. */
      this.warmCardImages(target, true);
      /* The switch animation runs on the PRODUCT PANEL only. Animating
         opacity/transform on the dialog (the video's ancestor) forces WebKit
         to tear down and rebuild the <video>'s compositing layer -- the
         residual once-per-navigation flicker. The video column is covered by
         its own poster placeholder during the source swap, so it needs no
         entrance animation of its own. */
      const host = this.panel?.closest('.sr-reels-product-panel') || this.panel;
      const outClass = direction > 0 ? 'sr-reels-switch-out-next' : 'sr-reels-switch-out-prev';
      const inClass = direction > 0 ? 'sr-reels-switch-in-next' : 'sr-reels-switch-in-prev';
      host?.classList.add(outClass);
      this.switchTimer = window.setTimeout(() => {
        host?.classList.remove(outClass);
        this.mountReel(target);
        host?.classList.add(inClass);
        this.switchTimer = window.setTimeout(() => {
          host?.classList.remove(inClass);
          this.isNavigating = false;
        }, 350);
      }, 170);
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.pausePreviews();
        this.modalVideo?.pause();
        this.stopProgressLoop(false, false);
      } else if (registry.activeController === this && this.isOpen) {
        const promise = this.modalVideo?.play();
        if (promise?.then) promise.then(() => this.runProgressLoop(this.modalVideo)).catch(() => {});
      } else if (registry.activeController) {
        this.pausePreviews();
      } else {
        this.resumePreviews();
      }
    }

    cardVideo(card) {
      return card?.querySelector('.sr-reels-preview-video') || null;
    }

    /* The modal owns its own <video>. Nothing is ever moved between the card
       and the modal, so there is no element to "restore" -- just stop the
       dedicated player and drop its source so it stops buffering. */
    restoreModalVideo() {
      const video = this.modalVideoEl;
      if (this.videoHost) this.videoHost.style.backgroundImage = '';
      if (!video) return;
      try {
        video.pause();
        video.removeAttribute('src');
        video.load();
      } catch (error) {}
      this.videoHost?.classList.remove('sr-reels-video-ready');
    }

    /* Created once and reused for every reel. Reparenting the card's own
       <video> was the cause of the Safari glitch: WebKit tears down and
       recreates the media player when a media element changes parent, so the
       last painted frame lingered, went black, then reloaded from zero. */
    ensureModalVideo() {
      if (this.modalVideoEl?.isConnected) return this.modalVideoEl;
      const video = document.createElement('video');
      video.className = 'sr-reels-modal-video';
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('disablepictureinpicture', '');
      video.setAttribute('disableremoteplayback', '');
      video.setAttribute('x-webkit-airplay', 'deny');
      video.setAttribute('controlslist', 'nodownload noplaybackrate noremoteplayback');
      video.controls = false;
      video.preload = 'auto';
      this.modalVideoEl = video;
      this.videoHost?.replaceChildren(video);
      return video;
    }

    adjacentCards() {
      const index = this.cards.indexOf(this.activeCard);
      if (index < 0 || this.cards.length < 2) return [];
      const prevIndex = (index - 1 + this.cards.length) % this.cards.length;
      const nextIndex = (index + 1) % this.cards.length;
      return [...new Set([this.cards[prevIndex], this.cards[nextIndex]])].filter(Boolean);
    }

    /* This used to set preload="auto" and call load() on the neighbouring
       reels -- immediately undoing the release that pausePreviews had just
       performed, and putting two more video downloads in front of the one the
       customer is actually watching. While a modal is open the modal video is
       the only thing allowed to stream; neighbours get metadata at most, which
       is enough for an instant first frame on navigation. */
    warmAdjacentVideos() {
      const connection = navigator.connection || navigator.webkitConnection;
      if (connection?.saveData || connection?.effectiveType === 'slow-2g' || connection?.effectiveType === '2g') return;
      this.adjacentCards().forEach((card) => {
        const video = this.cardVideo(card);
        if (!video || video === this.modalVideo) return;
        if (video.preload !== 'none') return;
        video.preload = 'metadata';
        video.setAttribute('preload', 'metadata');
      });
    }

    /* Warms EXACTLY the src/srcset/sizes triple the markup will use, so the
       request the browser makes on hover is the same one the <img> asks for a
       moment later. The old version set `srcset` (500w/1200w) *and* a 240w
       `src`; the browser ignores src when srcset is present, so it downloaded a
       1200px file and then the real <img> often asked for a different url
       anyway. Every one of those bytes was wasted. */
    warmImageResource(image, useSrcset, sizes, fetchPriority = 'low') {
      if (!imgKey(image)) return;
      const key = `${imgKey(image)}|${useSrcset ? sizes : 'thumb'}`;
      if (this.warmedImageUrls.has(key)) return;
      this.warmedImageUrls.add(key);
      const img = new Image();
      img.decoding = 'async';
      if (fetchPriority && 'fetchPriority' in img) img.fetchPriority = fetchPriority;
      if (useSrcset) {
        if (imgSet(image)) {
          img.srcset = imgSet(image);
          img.sizes = sizes;
        }
        /* `s` can be empty for older saved section payloads. In that case the
           old code warmed the 150px thumbnail although the modal requested
           the 600px image, so opening still triggered a cold download. */
        img.src = imgMain(image);
      } else {
        img.src = imgThumb(image);
      }
      /* Keep the Image object alive until the page is left. Some browsers may
         deprioritise an unattached Image after it becomes unreachable. */
      this.warmedImages.set(key, img);
    }

    /* Deliberately narrow: the first product's *displayed* picture plus the
       tiny gallery thumbs. It no longer pulls full-size copies of an entire
       8-image gallery, which used to fetch ~500px files that the thumbnail
       strip never even referenced. */
    warmProductsImages(products, includeGallery = false) {
      if (!Array.isArray(products)) return;
      const connection = navigator.connection || navigator.webkitConnection;
      const frugal = Boolean(connection?.saveData) || connection?.effectiveType === 'slow-2g' || connection?.effectiveType === '2g';
      const displayedSizes = products.length > 1 ? SR_CARD_SIZES : SR_MAIN_SIZES;
      products.slice(0, 4).forEach((product, productIndex) => {
        const primary = displayImageFor(product);
        if (productIndex === 0) {
          this.warmImageResource(primary, true, displayedSizes, 'high');
        } else if (!frugal) {
          this.warmImageResource(primary, true, SR_CARD_SIZES, 'low');
        }
        if (!includeGallery || productIndex > 0 || frugal) return;
        const images = Array.isArray(product?.images) ? product.images : [];
        images.slice(0, 8).forEach((image) => this.warmImageResource(image, false));
      });
    }

    warmCardImages(card, includeGallery = false) {
      if (!card) return;
      const data = this.readJson(document.getElementById(card.dataset.dataId));
      const products = Array.isArray(data?.products) ? data.products.filter(Boolean) : [];
      this.warmProductsImages(products, includeGallery);
    }

    warmAdjacentImages() {
      const connection = navigator.connection || navigator.webkitConnection;
      if (connection?.saveData || connection?.effectiveType === 'slow-2g' || connection?.effectiveType === '2g') return;
      this.adjacentCards().forEach((card) => this.warmCardImages(card, false));
    }

    /* Prefetching the neighbouring reel is a nicety; the product image the
       customer is staring at is not. Hold the prefetch until the main image
       has settled (or 1.5s has passed) so the two never compete for
       bandwidth during the modal-open window. */
    queueAdjacentWarm(video) {
      if (this.prefetchTimer) window.clearTimeout(this.prefetchTimer);
      const run = () => {
        this.prefetchTimer = 0;
        if (this.isOpen && this.modalVideo === video && !document.hidden) {
          this.warmAdjacentVideos();
          this.warmAdjacentImages();
        }
      };
      const start = () => {
        if (this.prefetchTimer) window.clearTimeout(this.prefetchTimer);
        this.prefetchTimer = window.setTimeout(run, 350);
      };
      const ready = this.mainImageReady;
      if (ready && typeof ready.then === 'function') {
        let settled = false;
        const once = () => { if (!settled) { settled = true; start(); } };
        ready.then(once, once);
        window.setTimeout(once, 1500);
      } else {
        start();
      }
    }

    togglePlayback() {
      const video = this.modalVideo;
      if (!video) return;
      if (video.paused || video.ended) {
        const promise = video.play();
        if (promise?.then) promise.then(() => this.runProgressLoop(video)).catch(() => {});
      } else {
        video.pause();
        this.cancelProgressClock();
      }
      this.syncPausedState();
    }

    syncPausedState() {
      const paused = Boolean(this.modalVideo?.paused);
      this.videoColumn?.classList.toggle('sr-reels-is-paused', paused);
    }

    /* Drag-to-scrub on the progress bar. Pointer events cover mouse and touch;
       the video is paused while scrubbing so seeking stays responsive, then
       resumes if it was playing. */
    scrubRatioFromEvent(event) {
      const rect = this.progressTrack.getBoundingClientRect();
      if (!rect.width) return 0;
      return Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    }

    applyScrub(event) {
      const video = this.modalVideo;
      if (!video) return;
      const duration = Number(video.duration);
      if (!Number.isFinite(duration) || duration <= 0) return;
      const ratio = this.scrubRatioFromEvent(event);
      try { video.currentTime = ratio * duration; } catch (error) {}
      if (this.progressFill) this.progressFill.style.width = `${(ratio * 100).toFixed(3)}%`;
      this.progressTrack?.setAttribute('aria-valuenow', String(Math.round(ratio * 100)));
    }

    bindScrubbing(signal) {
      const track = this.progressTrack;
      if (!track) return;
      let wasPlaying = false;
      track.addEventListener('pointerdown', (event) => {
        if (!this.modalVideo) return;
        event.preventDefault();
        this.scrubbing = true;
        wasPlaying = !this.modalVideo.paused;
        this.modalVideo.pause();
        track.classList.add('is-scrubbing');
        try { track.setPointerCapture(event.pointerId); } catch (error) {}
        this.applyScrub(event);
      }, { signal });
      track.addEventListener('pointermove', (event) => {
        if (this.scrubbing) this.applyScrub(event);
      }, { signal });
      const end = (event) => {
        if (!this.scrubbing) return;
        this.scrubbing = false;
        track.classList.remove('is-scrubbing');
        try { track.releasePointerCapture(event.pointerId); } catch (error) {}
        if (wasPlaying) {
          const promise = this.modalVideo?.play();
          if (promise?.then) promise.then(() => this.runProgressLoop(this.modalVideo)).catch(() => {});
        }
      };
      track.addEventListener('pointerup', end, { signal });
      track.addEventListener('pointercancel', end, { signal });
      /* Keyboard: arrows nudge 5s, home/end jump. */
      track.addEventListener('keydown', (event) => {
        const video = this.modalVideo;
        const duration = Number(video?.duration);
        if (!video || !Number.isFinite(duration) || duration <= 0) return;
        let time = null;
        if (event.key === 'ArrowRight') time = Math.min(duration, video.currentTime + 5);
        else if (event.key === 'ArrowLeft') time = Math.max(0, video.currentTime - 5);
        else if (event.key === 'Home') time = 0;
        else if (event.key === 'End') time = duration;
        else if (event.key === ' ' || event.key === 'Enter') { event.preventDefault(); this.togglePlayback(); return; }
        if (time == null) return;
        event.preventDefault();
        event.stopPropagation();
        try { video.currentTime = time; } catch (error) {}
        this.updateProgress(video);
      }, { signal });
    }

    updateProgress(video, mediaTime = null) {
      if (this.scrubbing) return;
      if (!this.progressFill || !video || this.modalVideo !== video) return;
      const duration = Number(video.duration);
      const resolvedTime = mediaTime == null ? Number(video.currentTime) : Number(mediaTime);
      const ratio = Number.isFinite(duration) && duration > 0 && Number.isFinite(resolvedTime)
        ? Math.max(0, Math.min(1, resolvedTime / duration))
        : 0;
      /* width, not transform: scaleX. A scaled element also scales its
         children (the drag handle) and interacts badly with the enlarged hit
         area, which is what left the bar looking like an empty pipe. */
      this.progressFill.style.removeProperty('transform');
      this.progressFill.style.width = `${(ratio * 100).toFixed(3)}%`;
      this.progressFill.dataset.progress = String(ratio);
    }

    cancelProgressClock() {
      if (this.progressFrame) cancelAnimationFrame(this.progressFrame);
      this.progressFrame = 0;
      if (this.progressVideoFrame && this.modalVideo?.cancelVideoFrameCallback) {
        try { this.modalVideo.cancelVideoFrameCallback(this.progressVideoFrame); } catch (error) {}
      }
      this.progressVideoFrame = 0;
    }

    stopProgressLoop(reset = false, detach = true) {
      this.cancelProgressClock();
      if (detach) {
        this.progressAbort?.abort();
        this.progressAbort = null;
      }
      if (reset && this.progressFill) {
        this.progressFill.style.removeProperty('transform');
        this.progressFill.style.width = '0%';
        this.progressFill.dataset.progress = '0';
      }
    }

    runProgressLoop(video) {
      if (!video || this.modalVideo !== video || !this.isOpen) return;
      this.cancelProgressClock();
      if (typeof video.requestVideoFrameCallback === 'function') {
        const tick = (now, metadata) => {
          this.progressVideoFrame = 0;
          if (this.modalVideo !== video || !this.isOpen) return;
          this.updateProgress(video, metadata?.mediaTime);
          if (!video.paused && !video.ended) this.progressVideoFrame = video.requestVideoFrameCallback(tick);
        };
        this.progressVideoFrame = video.requestVideoFrameCallback(tick);
        return;
      }
      const tick = () => {
        this.progressFrame = 0;
        if (this.modalVideo !== video || !this.isOpen) return;
        this.updateProgress(video);
        if (!video.paused && !video.ended) this.progressFrame = requestAnimationFrame(tick);
      };
      this.progressFrame = requestAnimationFrame(tick);
    }

    bindProgress(video) {
      this.stopProgressLoop(true, true);
      if (!video) return;
      this.progressAbort = new AbortController();
      const signal = this.progressAbort.signal;
      const update = () => this.updateProgress(video);
      ['loadstart', 'loadedmetadata', 'loadeddata', 'durationchange', 'canplay', 'playing', 'timeupdate', 'progress', 'ratechange', 'seeking', 'seeked'].forEach((eventName) => {
        video.addEventListener(eventName, update, { signal });
      });
      video.addEventListener('play', () => {
        update();
        this.runProgressLoop(video);
      }, { signal });
      video.addEventListener('pause', () => {
        this.cancelProgressClock();
        update();
      }, { signal });
      video.addEventListener('ended', () => {
        this.cancelProgressClock();
        update();
      }, { signal });
      video.addEventListener('emptied', () => {
        if (this.modalVideo === video && this.progressFill) this.progressFill.style.width = '0%';
      }, { signal });
      update();
      if (!video.paused) this.runProgressLoop(video);
    }

    mountReel(card) {
      const data = this.readJson(document.getElementById(card.dataset.dataId));
      const preview = this.cardVideo(card);
      if (!data || !preview) return false;
      this.stopProgressLoop(true);
      this.mainImageReady = null;
      this.galleryWarmQueue = [];
      if (this.galleryWarmTimer) { window.clearTimeout(this.galleryWarmTimer); this.galleryWarmTimer = 0; }
      this.modalVideo?.pause();
      this.restoreModalVideo();
      this.modalAbort?.abort();
      this.modalAbort = new AbortController();
      this.reelData = data;
      this.activeCard = card;
      /* Read the source off the card's preview but leave that element exactly
         where it is; the modal has its own player. */
      const source = preview.currentSrc || preview.getAttribute('src') || '';
      const poster = preview.getAttribute('poster') || '';
      const modalVideo = this.ensureModalVideo();
      this.modalVideo = modalVideo;
      preview.pause();
      this.releaseVideo(preview, true);

      /* Paint the product side FIRST. Title, price and the main <img> are in
         the document (and the image request is already in flight) before the
         video is asked to buffer, instead of queueing behind it. */
      this.shareButton.hidden = !(this.config.showShare && data.showShare);
      this.updateModalNavigation();
      this.dialog.setAttribute('aria-label', data.label || 'Shoppable reel');
      this.renderProducts();

      modalVideo.muted = this.userMuted;
      modalVideo.defaultMuted = this.userMuted;
      modalVideo.preload = 'auto';
      modalVideo.setAttribute('preload', 'auto');
      modalVideo.loop = Boolean(this.config.modalLoop);
      modalVideo.controls = false;
      modalVideo.removeAttribute('controls');
      this.videoHost.classList.remove('sr-reels-video-ready');
      /* Fill the load gap with this reel's poster frame. Without it the host
         is just black while Safari fetches the new source, which is the
         "glitch" -- a black rectangle flashing between two moving images. */
      this.videoHost.style.backgroundImage = poster ? `url("${poster}")` : '';
      if (poster) modalVideo.setAttribute('poster', poster);
      else modalVideo.removeAttribute('poster');
      if (source && modalVideo.getAttribute('src') !== source) {
        /* Setting src already triggers resource selection. Calling load() as
           well makes Safari abort and restart it -- a second teardown, and a
           second visible hitch. */
        modalVideo.setAttribute('src', source);
      } else {
        try {
          if (typeof modalVideo.fastSeek === 'function') modalVideo.fastSeek(0);
          else modalVideo.currentTime = 0;
        } catch (error) {}
      }
      /* Reveal only once a frame for THIS source is genuinely on screen.
         requestVideoFrameCallback is the precise signal where supported;
         elsewhere fall back to timeupdate past the first frame, with a timeout
         so a stalled network can never leave the panel blank forever. */
      let revealed = false;
      const reveal = () => {
        if (revealed) return;
        revealed = true;
        this.videoHost?.classList.add('sr-reels-video-ready');
      };
      if (typeof modalVideo.requestVideoFrameCallback === 'function') {
        try { modalVideo.requestVideoFrameCallback(() => reveal()); } catch (error) {}
      }
      modalVideo.addEventListener('timeupdate', function onTick() {
        if (modalVideo.currentTime > 0) {
          modalVideo.removeEventListener('timeupdate', onTick);
          reveal();
        }
      });
      modalVideo.addEventListener('playing', reveal, { once: true });
      window.setTimeout(reveal, 1200);
      this.bindProgress(modalVideo);
      const startVideo = () => {
        if (this.modalVideo !== modalVideo) return;
        try {
          if (typeof modalVideo.fastSeek === 'function') modalVideo.fastSeek(0);
          else modalVideo.currentTime = 0;
        } catch (error) {}
        this.updateProgress(modalVideo);
        const promise = modalVideo.play();
        if (promise?.then) {
          promise.then(() => {
            this.runProgressLoop(modalVideo);
            this.queueAdjacentWarm(modalVideo);
          }).catch(() => {});
        } else {
          this.runProgressLoop(modalVideo);
          this.queueAdjacentWarm(modalVideo);
        }
      };
      if (modalVideo.readyState >= 1) startVideo();
      else {
        if (modalVideo.readyState === 0) {
          try { modalVideo.load(); } catch (error) {}
        }
        modalVideo.addEventListener('loadedmetadata', startVideo, { once: true, signal: this.modalAbort.signal });
      }
      this.updateMuteButton();
      /* Secondary images (gallery thumbnails, other products) are warmed
         after the visible markup exists, never before it. */
      this.warmProductsImages(Array.isArray(data.products) ? data.products.filter(Boolean) : [], true);
      return true;
    }

    open(card) {
      if (!this.modal || this.isOpen) return;
      const data = this.readJson(document.getElementById(card.dataset.dataId));
      const preview = card.querySelector('.sr-reels-preview-video');
      if (!data || !preview) return;
      const previousController = registry.activeController;
      registry.activeController = this;
      if (previousController && previousController !== this) previousController.close({ restoreFocus: false, immediate: true });
      this.closeToken += 1;
      this.isOpen = true;
      this.mobileMode = this.isMobileViewport();
      this.userMuted = true;
      this.hasUnmuted = false;
      registry.controllers.forEach((controller) => controller.pausePreviews());
      this.modal.hidden = false;
      lockPage();
      const openIndex = Math.max(0, this.cards.indexOf(card));
      const mounted = this.mobileMode ? this.buildMobileStack(openIndex) : this.mountReel(card);
      if (!mounted) {
        this.isOpen = false;
        this.mobileMode = false;
        this.modal.hidden = true;
        unlockPage();
        if (registry.activeController === this) registry.activeController = null;
        return;
      }
      this.setMuted(true, true);
      requestAnimationFrame(() => {
        this.modal.classList.add('sr-reels-is-open');
        this.closeButton?.focus({ preventScroll: true });
      });
      this.maybeShowSwipeHint();
    }

    close(options = {}) {
      if (!this.isOpen) return;
      const restoreFocus = options.restoreFocus !== false;
      const immediate = Boolean(options.immediate);
      const token = ++this.closeToken;
      const card = this.activeCard;
      const wasMobile = this.mobileMode;
      this.isOpen = false;
      this.isNavigating = false;
      this.hideSwipeHint(false);
      if (this.switchTimer) window.clearTimeout(this.switchTimer);
      this.switchTimer = 0;
      if (this.prefetchTimer) window.clearTimeout(this.prefetchTimer);
      this.prefetchTimer = 0;
      this.stopProgressLoop(true);
      this.dialog?.classList.remove('sr-reels-sheet-open');
      const panelHost = this.panel?.closest('.sr-reels-product-panel') || this.panel;
      panelHost?.classList.remove('sr-reels-switch-out-next', 'sr-reels-switch-out-prev', 'sr-reels-switch-in-next', 'sr-reels-switch-in-prev');
      this.modalVideo?.pause();
      this.modalAbort?.abort();
      this.modalAbort = null;
      this.modal?.classList.remove('sr-reels-is-open');
      unlockPage();
      if (registry.activeController === this) registry.activeController = null;
      const finish = () => {
        if (token !== this.closeToken || this.isOpen) return;
        if (wasMobile) this.clearMobileStack();
        else this.restoreModalVideo();
        if (this.modal) this.modal.hidden = true;
        this.panel?.replaceChildren();
        this.modalVideo = null;
        this.singleProduct = null;
        this.activeProduct = null;
        this.mainImageReady = null;
        this.reelData = null;
        this.mobileMode = false;
        if (!registry.activeController) registry.controllers.forEach((controller) => controller.resumePreviews());
      };
      if (immediate || this.reducedMotion) finish();
      else window.setTimeout(finish, 240);
      if (restoreFocus && card?.isConnected) card.focus({ preventScroll: true });
      this.activeCard = null;
    }

    setMuted(muted, initial = false) {
      this.userMuted = Boolean(muted);
      if (this.modalVideo) {
        this.modalVideo.muted = this.userMuted;
        this.modalVideo.defaultMuted = this.userMuted;
      }
      if (initial && !this.hasUnmuted) {
        this.unmuteButton.hidden = false;
        this.muteButton.hidden = true;
      } else {
        this.hasUnmuted = true;
        this.unmuteButton.hidden = true;
        this.muteButton.hidden = false;
        this.updateMuteButton();
      }
      if (!initial && this.modalVideo?.paused) {
        const playPromise = this.modalVideo.play();
        if (playPromise?.then) playPromise.then(() => this.runProgressLoop(this.modalVideo)).catch(() => {});
      }
    }

    updateMuteButton() {
      if (!this.muteButton) return;
      this.muteButton.innerHTML = this.userMuted ? icons.muted : icons.sound;
      this.muteButton.setAttribute('aria-label', this.userMuted ? 'Unmute reel video' : 'Mute reel video');
    }

    onKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (!this.mobileMode && (event.key === 'ArrowLeft' || event.key === 'ArrowRight') && !event.target.matches('input, select, textarea')) {
        event.preventDefault();
        this.navigateModal(event.key === 'ArrowLeft' ? -1 : 1);
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(this.modal.querySelectorAll('button:not([disabled]):not([hidden]), a[href], select:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'))
        .filter((node) => node.offsetParent !== null);
      if (!focusable.length) {
        event.preventDefault();
        this.dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    money(cents) {
      const value = Number(cents || 0);
      const format = this.config.moneyFormat || (String.fromCharCode(36, 123, 123) + 'amount' + String.fromCharCode(125, 125));
      const withDelimiters = (number, decimals, thousands, decimal) => {
        const fixed = (number / 100).toFixed(decimals);
        const parts = fixed.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return parts.length > 1 ? parts[0] + decimal + parts[1] : parts[0];
      };
      return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, token) => {
        if (token === 'amount_no_decimals') return withDelimiters(value, 0, ',', '.');
        if (token === 'amount_with_comma_separator') return withDelimiters(value, 2, '.', ',');
        if (token === 'amount_no_decimals_with_comma_separator') return withDelimiters(value, 0, '.', ',');
        if (token === 'amount_with_apostrophe_separator') return withDelimiters(value, 2, "'", '.');
        return withDelimiters(value, 2, ',', '.');
      });
    }

    priceMarkup(price, compareAt) {
      const sale = Number(compareAt) > Number(price);
      return `<div class="sr-reels-price-row">
        <span class="sr-reels-price-current">${escapeHtml(this.money(price))}</span>
        ${sale ? `<span class="sr-reels-price-compare">${escapeHtml(this.money(compareAt))}</span><span class="sr-reels-sale-badge">Sale</span>` : ''}
      </div>`;
    }

    /* srcset:false => a single fixed 150w src (thumbnails, where a responsive
       ladder is pure overhead).
       lqip => paint the 150w thumbnail behind the image while the full one
       arrives. That url is the same one the thumbnail strip requests, so it
       costs zero extra round trips. */
    imageMarkup(image, className = '', options = {}) {
      if (!imgKey(image)) return '<span class="sr-reels-image-placeholder">No image available</span>';
      const {
        srcset = true,
        sizes = SR_CARD_SIZES,
        loading = 'eager',
        fetchPriority = 'auto',
        lqip = false
      } = options;
      const setValue = srcset ? imgSet(image) : '';
      const imageUrl = srcset ? imgMain(image) : imgThumb(image);
      const width = Number(image.width) || 1000;
      const height = Number(image.height) || 1000;
      const srcsetAttr = setValue ? ` srcset="${escapeHtml(setValue)}" sizes="${escapeHtml(sizes)}"` : '';
      const classes = `sr-reels-fade-img sr-reels-img-pending${className ? ` ${className}` : ''}`;
      const previewStyle = lqip && imgThumb(image) ? ` style="background-image:url('${escapeHtml(imgThumb(image))}')"` : '';
      const priorityAttr = fetchPriority && fetchPriority !== 'auto' ? ` fetchpriority="${escapeHtml(fetchPriority)}"` : '';
      return `<img class="${escapeHtml(classes)}" src="${escapeHtml(imageUrl)}"${srcsetAttr}${previewStyle}${priorityAttr} alt="${escapeHtml(image.alt || '')}" width="${width}" height="${height}" loading="${escapeHtml(loading)}" decoding="async">`;
    }

    /* Reveal never depends on a single event firing.
       `load` can be missed and decode() can reject, which is what left images
       stuck behind the blur until an unrelated click forced a repaint. We now
       ALSO poll img.complete, so the moment the browser actually has the
       bitmap the placeholder comes off, no user interaction required. */
    wireFadeImages(scope) {
      const images = scope?.querySelectorAll ? scope.querySelectorAll('img.sr-reels-fade-img') : [];
      const settled = [];
      images.forEach((img) => {
        let done = false;
        let poll = 0;
        const settle = () => {
          if (done) return;
          done = true;
          if (poll) window.clearInterval(poll);
          poll = 0;
          img.classList.remove('sr-reels-img-pending');
          img.classList.add('sr-reels-img-loaded');
        };
        settled.push(new Promise((resolve) => {
          const finish = () => { settle(); resolve(); };
          if (img.complete && img.naturalWidth > 0) { finish(); return; }
          img.addEventListener('load', finish, { once: true });
          img.addEventListener('error', finish, { once: true });
          if (typeof img.decode === 'function') img.decode().then(finish, () => {});
          /* Backstop: catches any case where neither event is delivered. */
          let ticks = 0;
          poll = window.setInterval(() => {
            ticks += 1;
            if ((img.complete && img.naturalWidth > 0) || !img.isConnected || ticks > 200) finish();
          }, 100);
        }));
      });
      return settled.length ? Promise.all(settled) : Promise.resolve();
    }

    /* Once the main image is on screen, quietly fetch the full-size version of
       the remaining gallery pictures at low priority. Clicking a thumbnail then
       swaps to an already-cached file instead of starting a fresh download,
       which is what made thumbnail switching sit on a blurred placeholder. */
    /* Firing all eight gallery images at once starved the modal video of
       bandwidth and made it stall mid-playback. They are now fetched ONE AT A
       TIME, and each one waits until the video has a comfortable buffer ahead
       of the playhead, so prefetching can never interrupt what is on screen. */
    warmGalleryMains(product) {
      const images = Array.isArray(product?.images) ? product.images : [];
      if (images.length < 2) return;
      const connection = navigator.connection || navigator.webkitConnection;
      if (connection?.saveData || connection?.effectiveType === 'slow-2g' || connection?.effectiveType === '2g') return;
      this.galleryWarmQueue = images.slice(0, 8).filter((image) => {
        const key = `${imgKey(image)}|main`;
        if (!imgKey(image) || this.warmedImageUrls.has(key)) return false;
        this.warmedImageUrls.add(key);
        return true;
      });
      this.galleryWarmAttempts = 0;
      this.pumpGalleryWarm();
    }

    /* Seconds of media buffered ahead of the playhead. */
    videoBufferAhead(video) {
      if (!video) return Infinity;
      const buffered = video.buffered;
      for (let index = 0; index < buffered.length; index += 1) {
        if (buffered.start(index) <= video.currentTime && video.currentTime <= buffered.end(index)) {
          return buffered.end(index) - video.currentTime;
        }
      }
      return 0;
    }

    pumpGalleryWarm() {
      if (this.galleryWarmTimer) { window.clearTimeout(this.galleryWarmTimer); this.galleryWarmTimer = 0; }
      if (!this.isOpen || !this.galleryWarmQueue.length) return;
      const video = this.modalVideo;
      /* Hold off while the video is playing and short on buffer. The attempt
         counter stops this deferring forever on a long or slow video. */
      if (video && !video.paused && this.videoBufferAhead(video) < 4 && this.galleryWarmAttempts < 12) {
        this.galleryWarmAttempts += 1;
        this.galleryWarmTimer = window.setTimeout(() => this.pumpGalleryWarm(), 500);
        return;
      }
      const image = this.galleryWarmQueue.shift();
      if (!image) return;
      const img = new Image();
      img.decoding = 'async';
      if ('fetchPriority' in img) img.fetchPriority = 'low';
      const next = () => {
        this.galleryWarmTimer = window.setTimeout(() => this.pumpGalleryWarm(), 80);
      };
      img.addEventListener('load', next, { once: true });
      img.addEventListener('error', next, { once: true });
      img.src = imgMain(image);
    }

    renderProducts() {
      const products = Array.isArray(this.reelData?.products) ? this.reelData.products.filter(Boolean) : [];
      this.dialog.classList.toggle('sr-reels-no-products', !products.length);
      this.updatePeekBar(products);
      if (!products.length) {
        this.panel.replaceChildren();
        return;
      }
      if (products.length === 1) this.renderSingleProduct(products[0]);
      else this.renderMultipleProducts(products);
    }

    updatePeekBar(products) {
      if (!this.peekBar) return;
      const product = products[0];
      if (!product) {
        this.peekBar.hidden = true;
        return;
      }
      this.peekBar.hidden = false;
      const imageUrl = imgThumb(displayImageFor(product) || product.featuredImage);
      if (this.peekImage) {
        if (imageUrl) {
          this.peekImage.src = imageUrl;
          this.peekImage.hidden = false;
        } else {
          this.peekImage.hidden = true;
        }
      }
      if (this.peekTitle) {
        const extra = products.length > 1 ? ` +${products.length - 1} more` : '';
        this.peekTitle.textContent = `${product.title || ''}${extra}`;
      }
      if (this.peekPrice) {
        const variant = Array.isArray(product.variants) ? (product.variants.find((item) => item.available) || product.variants[0]) : null;
        this.peekPrice.textContent = this.money(variant?.price ?? product.price);
      }
    }

    renderSingleProduct(product) {
      const variant = product.variants.find((item) => item.available) || product.variants[0] || null;
      this.activeProduct = product;
      this.singleProduct = { product, variant };
      const images = Array.isArray(product.images) ? product.images : [];
      const mainImage = resolveImage(product, variant?.img) || product.featuredImage || images[0];
      const slides = images.length ? images : (mainImage ? [mainImage] : []);
      const startIndex = Math.max(0, slides.findIndex((image) => imgKey(image) === imgKey(mainImage)));

      /* Swipeable image carousel with peeking neighbours, replacing the large
         main image + thumbnail strip. Only the first slide is high priority;
         the rest are lazy so a long gallery cannot flood the connection. */
      const carousel = this.config.showGallery && slides.length ? `
        <div class="sr-reels-carousel" data-sr-carousel>
          <div class="sr-reels-carousel-track" data-sr-carousel-track tabindex="0" role="group" aria-label="Product images">
            ${slides.map((image, index) => `
              <div class="sr-reels-carousel-slide" data-slide-index="${index}">
                ${this.imageMarkup(image, 'sr-reels-carousel-image', {
                  srcset: true,
                  sizes: SR_MAIN_SIZES,
                  /* The next slide peeks into view, so it is genuinely visible
                     and must not be deferred behind lazy loading. */
                  loading: Math.abs(index - startIndex) <= 1 ? 'eager' : 'lazy',
                  fetchPriority: index === startIndex ? 'high' : 'low',
                  lqip: true
                })}
              </div>`).join('')}
          </div>
          ${slides.length > 1 ? `<div class="sr-reels-carousel-progress" data-sr-carousel-progress aria-hidden="true">
            ${slides.map((_, index) => `<span class="sr-reels-carousel-seg${index === startIndex ? ' is-active' : ''}" data-seg-index="${index}"></span>`).join('')}
          </div>` : ''}
        </div>` : '';

      /* Variant pickers are swatch buttons now instead of <select> menus.
         Combinations that do not exist stay visible but disabled, matching the
         greyed-out sizes in the reference. */
      const optionsMarkup = !product.hasOnlyDefaultVariant ? product.options.map((option, optionIndex) => `
        <div class="sr-reels-option-group" data-option-index="${optionIndex}">
          <span class="sr-reels-field-label">${escapeHtml(option.name)}:</span>
          <div class="sr-reels-swatches" role="group" aria-label="${escapeHtml(option.name)}">
            ${option.values.map((value) => {
              const exists = product.variants.some((item) => item.options?.[optionIndex] === value);
              const inStock = product.variants.some((item) => item.options?.[optionIndex] === value && item.available);
              const selected = variant?.options?.[optionIndex] === value;
              return `<button class="sr-reels-swatch" type="button" data-sr-option-value data-option-index="${optionIndex}" data-value="${escapeHtml(value)}" aria-pressed="${selected}"${exists && inStock ? '' : ' disabled'}>${escapeHtml(value)}</button>`;
            }).join('')}
          </div>
        </div>`).join('') : '';

      const available = Boolean(variant?.available);
      const productUrl = safeUrl(product.url, true);
      const description = this.config.showDescription && product.description
        ? (this.config.collapseDescription === false
          ? `<p class="sr-reels-description">${escapeHtml(product.description)}</p>`
          : `<details class="sr-reels-accordion"><summary>Description</summary><p class="sr-reels-description">${escapeHtml(product.description)}</p></details>`)
        : '';

      this.panel.innerHTML = `
        <div class="sr-reels-single-product">
          ${carousel}
          <div class="sr-reels-product-meta">
            <h2 class="sr-reels-product-title" id="sr-reels-dialog-title-${escapeHtml(this.id)}">${escapeHtml(product.title)}</h2>
            <div data-single-price>${this.priceMarkup(variant?.price ?? product.price, variant?.compareAtPrice ?? product.compareAtPrice)}</div>
            <p class="sr-reels-availability" data-single-availability data-available="${available}">${available ? 'In stock' : 'Sold out'}</p>
            ${optionsMarkup}
            <div class="sr-reels-quantity-row">
              <span class="sr-reels-field-label">Qty:</span>
              <div class="sr-reels-quantity" aria-label="Quantity selector">
                <button class="sr-reels-quantity-button" type="button" data-quantity-change="-1" aria-label="Decrease quantity"><span class="sr-reels-quantity-symbol" aria-hidden="true">&minus;</span></button>
                <input class="sr-reels-quantity-input" type="number" min="1" max="999" value="1" inputmode="numeric" aria-label="Quantity">
                <button class="sr-reels-quantity-button" type="button" data-quantity-change="1" aria-label="Increase quantity"><span class="sr-reels-quantity-symbol" aria-hidden="true">+</span></button>
              </div>
            </div>
            <div class="sr-reels-action-row">
              ${productUrl ? `<a class="sr-reels-secondary-button" href="${escapeHtml(productUrl)}">
                <svg class="sr-reels-inline-icon" aria-hidden="true" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.6"/><path d="M12 11v5.5M12 7.6v.9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
                <span>${escapeHtml(this.reelData.ctaLabel || 'More Info')}</span>
              </a>` : ''}
              <button class="sr-reels-primary-button" type="button" data-add-to-cart data-variant-id="${variant?.id || ''}" ${available ? '' : 'disabled'}>
                <svg class="sr-reels-inline-icon" aria-hidden="true" viewBox="0 0 24 24" fill="none"><path d="M6 8h12l-1 11H7L6 8Z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M9.5 10V7a2.5 2.5 0 0 1 5 0v3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
                <span>${available ? 'Add to Cart' : 'Sold out'}</span>
              </button>
            </div>
            <div class="sr-reels-feedback" role="status" aria-live="polite"></div>
            ${description}
          </div>
        </div>`;

      this.setupCarousel(startIndex);
      this.mainImageReady = this.wireFadeImages(this.panel);
      const target = product;
      this.mainImageReady.then(() => {
        if (this.singleProduct?.product === target) this.warmGalleryMains(target);
      }, () => {});
    }

    /* Carousel plumbing. Scroll-snap does the moving; JS only mirrors the
       active slide into the progress bar and handles programmatic jumps when a
       variant changes. */
    setupCarousel(startIndex = 0) {
      this.carouselTrack = this.panel.querySelector('[data-sr-carousel-track]');
      this.carouselSegs = Array.from(this.panel.querySelectorAll('.sr-reels-carousel-seg'));
      if (!this.carouselTrack) return;
      if (startIndex > 0) this.scrollCarouselTo(startIndex, 'auto');
      this.carouselTrack.addEventListener('scroll', () => {
        if (this.carouselFrame) return;
        this.carouselFrame = requestAnimationFrame(() => {
          this.carouselFrame = 0;
          this.syncCarouselProgress();
        });
      }, { passive: true, signal: this.modalAbort?.signal });
      this.syncCarouselProgress();
    }

    carouselIndex() {
      const track = this.carouselTrack;
      if (!track) return 0;
      const slide = track.querySelector('.sr-reels-carousel-slide');
      const step = slide ? slide.getBoundingClientRect().width + 10 : track.clientWidth;
      return step > 0 ? Math.round(track.scrollLeft / step) : 0;
    }

    syncCarouselProgress() {
      if (!this.carouselSegs?.length) return;
      const active = Math.max(0, Math.min(this.carouselSegs.length - 1, this.carouselIndex()));
      this.carouselSegs.forEach((seg, index) => seg.classList.toggle('is-active', index === active));
    }

    scrollCarouselTo(index, behavior = 'smooth') {
      const track = this.carouselTrack;
      if (!track) return;
      const slide = track.querySelectorAll('.sr-reels-carousel-slide')[index];
      if (!slide) return;
      track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: this.reducedMotion ? 'auto' : behavior });
      this.syncCarouselProgress();
    }

    actionConfig() {
      const raw = String(this.config.multipleAction || 'open_shop');
      const quick = raw.startsWith('quick_');
      const suffix = raw.split('_')[1] || 'shop';
      const labels = { shop: 'Shop now', view: 'View product', more: 'More info' };
      return { quick, label: labels[suffix] || 'Shop now' };
    }

    renderMultipleProducts(products) {
      const action = this.actionConfig();
      const customLabel = this.reelData.ctaLabel;
      this.panel.innerHTML = `
        <h2 class="sr-reels-panel-heading" id="sr-reels-dialog-title-${escapeHtml(this.id)}">Products seen in the video</h2>
        <div class="sr-reels-products-grid">
          ${products.map((product, productIndex) => {
            const availableVariants = product.variants.filter((variant) => variant.available);
            const hasMultipleAvailable = availableVariants.length > 1;
            const fixedVariant = availableVariants.length === 1 ? availableVariants[0] : null;
            const displayVariant = fixedVariant || availableVariants[0] || product.variants[0] || null;
            /* The grid is 2 columns, so the first four cards are what the
               customer can actually see. Those load eagerly; anything below
               the fold is left to lazy loading instead of everything racing
               at once. */
            const aboveFold = productIndex < 4;
            const imageContent = this.imageMarkup(
              resolveImage(product, displayVariant?.img) || product.featuredImage,
              '',
              {
                srcset: true,
                sizes: SR_CARD_SIZES,
                loading: aboveFold ? 'eager' : 'lazy',
                fetchPriority: productIndex < 2 ? 'high' : 'low'
              }
            );
            const productUrl = safeUrl(product.url, true);
            const image = productUrl ? `<a class="sr-reels-product-card-image" href="${escapeHtml(productUrl)}">${imageContent}</a>` : `<div class="sr-reels-product-card-image">${imageContent}</div>`;
            const title = productUrl ? `<a href="${escapeHtml(productUrl)}">${escapeHtml(product.title)}</a>` : escapeHtml(product.title);
            let actionMarkup = '';
            if (action.quick) {
              const selector = hasMultipleAvailable ? `<label class="sr-reels-field-label" for="sr-reels-quick-${escapeHtml(this.id)}-${productIndex}">Choose an option</label>
                <select class="sr-reels-select sr-reels-quick-variant" id="sr-reels-quick-${escapeHtml(this.id)}-${productIndex}">
                  ${product.variants.map((variant) => `<option value="${variant.id}" data-price="${variant.price}" data-compare-at="${variant.compareAtPrice || 0}"${variant.available ? '' : ' disabled'}${variant.id === availableVariants[0]?.id ? ' selected' : ''}>${escapeHtml(variant.title)} — ${escapeHtml(this.money(variant.price))}${variant.available ? '' : ' — Sold out'}</option>`).join('')}
                </select>` : '';
              actionMarkup = `${selector}<button class="sr-reels-primary-button" type="button" data-add-to-cart data-variant-id="${fixedVariant?.id || ''}" ${availableVariants.length ? '' : 'disabled'}>${availableVariants.length ? (customLabel || 'Quick add') : 'Sold out'}</button>`;
            } else if (productUrl) {
              actionMarkup = `<a class="sr-reels-primary-button" href="${escapeHtml(productUrl)}">${escapeHtml(customLabel || action.label)}</a>`;
            }
            return `<article class="sr-reels-product-card" data-product-index="${productIndex}">
              ${image}
              <h3 class="sr-reels-product-title">${title}</h3>
              <div data-quick-price>${this.priceMarkup(displayVariant?.price ?? product.price, displayVariant?.compareAtPrice ?? product.compareAtPrice)}</div>
              <p class="sr-reels-availability" data-available="${product.available}">${product.available ? 'Available' : 'Sold out'}</p>
              ${actionMarkup}
              <div class="sr-reels-feedback" role="status" aria-live="polite"></div>
            </article>`;
          }).join('')}
        </div>`;
      this.mainImageReady = this.wireFadeImages(this.panel);
    }

    onPanelClick(event) {
      const swatch = event.target.closest('[data-sr-option-value]');
      if (swatch && !swatch.disabled) {
        const group = swatch.closest('.sr-reels-option-group');
        group?.querySelectorAll('[data-sr-option-value]').forEach((button) => {
          button.setAttribute('aria-pressed', String(button === swatch));
        });
        this.updateSingleVariant();
        return;
      }
      const seg = event.target.closest('[data-seg-index]');
      if (seg) {
        this.scrollCarouselTo(Number(seg.dataset.segIndex));
        return;
      }
      const quantityButton = event.target.closest('[data-quantity-change]');
      if (quantityButton) {
        const input = quantityButton.parentElement.querySelector('.sr-reels-quantity-input');
        const next = Math.min(999, Math.max(1, (Number.parseInt(input.value, 10) || 1) + Number(quantityButton.dataset.quantityChange)));
        input.value = String(next);
        return;
      }
      const addButton = event.target.closest('[data-add-to-cart]');
      if (addButton) {
        const card = addButton.closest('.sr-reels-product-card');
        const selector = card?.querySelector('.sr-reels-quick-variant');
        const variantId = selector?.value || addButton.dataset.variantId;
        const quantity = Number.parseInt(this.panel.querySelector('.sr-reels-quantity-input')?.value || '1', 10);
        this.addToCart(variantId, Math.min(999, Math.max(1, quantity || 1)), addButton);
      }
    }

    onPanelChange(event) {
      if (event.target.matches('.sr-reels-quick-variant')) {
        const option = event.target.selectedOptions[0];
        const priceHost = event.target.closest('.sr-reels-product-card')?.querySelector('[data-quick-price]');
        if (option && priceHost) priceHost.innerHTML = this.priceMarkup(option.dataset.price, option.dataset.compareAt);
      }
    }

    updateSingleVariant() {
      if (!this.singleProduct) return;
      const product = this.singleProduct.product;
      const groups = Array.from(this.panel.querySelectorAll('.sr-reels-option-group'));
      const values = groups.map((group) => group.querySelector('[data-sr-option-value][aria-pressed="true"]')?.dataset.value ?? null);
      const variant = product.variants.find((item) => item.options.every((value, index) => values[index] == null || value === values[index])) || null;
      this.singleProduct.variant = variant;
      const available = Boolean(variant?.available);
      const priceHost = this.panel.querySelector('[data-single-price]');
      const availability = this.panel.querySelector('[data-single-availability]');
      const addButton = this.panel.querySelector('[data-add-to-cart]');
      if (priceHost && variant) priceHost.innerHTML = this.priceMarkup(variant.price, variant.compareAtPrice);
      if (availability) {
        availability.textContent = variant ? (available ? 'In stock' : 'Sold out') : 'Unavailable combination';
        availability.dataset.available = String(available);
      }
      if (addButton) {
        addButton.dataset.variantId = variant?.id || '';
        addButton.disabled = !available;
        const label = addButton.querySelector('span') || addButton;
        label.textContent = available ? 'Add to Cart' : (variant ? 'Sold out' : 'Unavailable');
      }
      /* Selecting a variant scrolls the carousel to that variant's picture
         rather than replacing the main image, so the gallery stays intact. */
      const variantImage = resolveImage(product, variant?.img);
      if (variantImage) {
        const images = Array.isArray(product.images) ? product.images : [];
        const index = images.findIndex((image) => imgKey(image) === imgKey(variantImage));
        if (index >= 0) this.scrollCarouselTo(index);
      }
    }

    async refreshCartCount(root) {
      try {
        const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) return null;
        const cart = await response.json();
        const count = Number(cart.item_count) || 0;
        document.querySelectorAll('[data-cart-count], [data-cart-item-count], .cart-count-bubble span[aria-hidden="true"]').forEach((node) => {
          node.textContent = String(count);
          if (node.hasAttribute('data-cart-count')) node.setAttribute('data-cart-count', String(count));
          if (node.hasAttribute('data-cart-item-count')) node.setAttribute('data-cart-item-count', String(count));
        });
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
        return cart;
      } catch (error) {
        return null;
      }
    }

    openCartSurface(payload) {
      const state = { ...payload, id: payload?.id || payload?.items?.[0]?.id };
      const drawer = document.querySelector('cart-drawer');
      const isDawnDrawer = drawer && typeof drawer.renderContents === 'function' && typeof drawer.getSectionsToRender === 'function';
      if (isDawnDrawer && payload?.sections?.['cart-drawer']) {
        try {
          drawer.renderContents(state);
          if (typeof drawer.open === 'function') drawer.open();
          return true;
        } catch (error) {}
      }

      const notification = document.querySelector('cart-notification');
      if (notification && typeof notification.renderContents === 'function' && payload?.sections?.['cart-notification']) {
        try {
          notification.renderContents(state);
          if (typeof notification.open === 'function') notification.open();
          return true;
        } catch (error) {}
      }

      if (drawer && typeof drawer.open === 'function') {
        window.setTimeout(() => drawer.open(), 80);
        return true;
      }

      const toggle = document.querySelector('[aria-controls="CartDrawer"], [aria-controls="cart-drawer"], [data-cart-drawer-toggle], [data-cart-toggle]');
      if (toggle instanceof HTMLElement) {
        window.setTimeout(() => {
          const controlledId = toggle.getAttribute('aria-controls');
          const controlled = controlledId ? document.getElementById(controlledId) : null;
          const isOpen = toggle.getAttribute('aria-expanded') === 'true'
            || controlled?.hasAttribute('open')
            || controlled?.classList.contains('open')
            || controlled?.classList.contains('active')
            || controlled?.classList.contains('is-open');
          if (!isOpen) toggle.click();
        }, 100);
        return true;
      }

      document.dispatchEvent(new CustomEvent('shoppable-reels:open-cart', { detail: { response: payload } }));
      return false;
    }

    async addToCart(variantId, quantity, button) {
      const id = Number(variantId);
      if (!Number.isFinite(id) || id <= 0 || button.disabled) return;
      const feedback = button.closest('.sr-reels-mobile-product-card')?.querySelector('.sr-reels-mobile-feedback') || button.parentElement?.querySelector('.sr-reels-feedback') || button.closest('.sr-reels-product-card')?.querySelector('.sr-reels-feedback') || this.panel?.querySelector('.sr-reels-feedback');
      const original = button.textContent;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'Adding…';
      if (feedback) feedback.textContent = '';
      try {
        const root = shopRouteRoot();
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id, quantity }],
            sections: ['cart-drawer', 'cart-icon-bubble', 'cart-notification'],
            sections_url: window.location.pathname
          })
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.description || payload.message || 'Unable to add this item.');
        button.textContent = 'Added to cart';
        const cart = await this.refreshCartCount(root);
        const detail = { sectionId: this.id, variantId: id, quantity, response: payload };
        detail.cart = cart;
        this.root.dispatchEvent(new CustomEvent('shoppable-reels:cart-added', { bubbles: true, detail }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail }));
        if (cart) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'variant:add', cart }
          }));
        }
        if (this.config.afterAdd === 'drawer') {
          this.close();
          const opened = this.openCartSurface(payload);
          if (!opened) window.setTimeout(() => { window.location.href = `${root}cart`; }, 500);
        } else if (this.config.afterAdd === 'close') {
          window.setTimeout(() => this.close(), 650);
        } else if (this.config.afterAdd === 'cart') {
          window.setTimeout(() => { window.location.href = `${root}cart`; }, 650);
        } else {
          window.setTimeout(() => {
            if (!button.isConnected) return;
            button.disabled = false;
            button.removeAttribute('aria-busy');
            button.textContent = original;
          }, 1400);
        }
      } catch (error) {
        if (feedback) feedback.textContent = error?.message || 'Something went wrong. Please try again.';
        button.textContent = 'Try again';
        window.setTimeout(() => {
          if (!button.isConnected) return;
          button.disabled = false;
          button.removeAttribute('aria-busy');
          button.textContent = original;
        }, 1400);
      }
    }

    async share() {
      const title = this.reelData?.products?.[0]?.title || this.reelData?.label || document.title;
      const shareData = { title, text: `Take a look at ${title}`, url: window.location.href };
      try {
        if (navigator.share) await navigator.share(shareData);
        else {
          if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(window.location.href);
          else this.fallbackCopy(window.location.href);
          this.showShareStatus('Link copied');
        }
      } catch (error) {
        if (error?.name !== 'AbortError') {
          try { this.fallbackCopy(window.location.href); this.showShareStatus('Link copied'); } catch (copyError) { this.showShareStatus('Unable to copy link'); }
        }
      }
    }

    fallbackCopy(value) {
      const input = document.createElement('textarea');
      input.value = value;
      input.setAttribute('readonly', '');
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      const copied = document.execCommand('copy');
      input.remove();
      if (!copied) throw new Error('Copy failed');
    }

    showShareStatus(message) {
      if (!this.shareStatus) return;
      this.shareStatus.textContent = message;
      this.shareStatus.hidden = false;
      window.setTimeout(() => { if (this.shareStatus) this.shareStatus.hidden = true; }, 1800);
    }

    handleBlockSelect(blockId) {
      const wrap = Array.from(this.root.querySelectorAll('[data-block-id]')).find((node) => node.dataset.blockId === String(blockId));
      if (!wrap) return;
      wrap.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
      const video = wrap.querySelector('.sr-reels-preview-video');
      if (video && !this.isOpen) this.safePlay(video);
    }

    handleBlockDeselect() {
      this.updateNavigation();
      if (!this.isOpen && !document.hidden) this.visibleVideos.forEach((video) => this.safePlay(video));
    }

    destroy() {
      if (this.isOpen) this.close({ restoreFocus: false, immediate: true });
      this.abort.abort();
      this.observer?.disconnect();
      this.imageWarmObserver?.disconnect();
      this.imageWarmObserver = null;
      this.stopProgressLoop(false);
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      if (this.mobileScrollFrame) cancelAnimationFrame(this.mobileScrollFrame);
      if (this.prefetchTimer) window.clearTimeout(this.prefetchTimer);
      if (this.switchTimer) window.clearTimeout(this.switchTimer);
      this.videos.forEach((video) => video.pause());
      this.clearMobileStack();
      this.restoreModalVideo();
    }

  }

  const init = (root) => {
    if (!root?.matches?.('[data-sr-reels-section]')) return;
    const id = root.dataset.sectionId;
    registry.controllers.get(id)?.destroy();
    registry.controllers.set(id, new SrReelsController(root));
  };

  if (!registry.listenersReady) {
    registry.listenersReady = true;
    document.addEventListener('shopify:section:load', (event) => {
      const root = event.target.matches?.('[data-sr-reels-section]') ? event.target : event.target.querySelector?.('[data-sr-reels-section]');
      if (root) init(root);
    });
    document.addEventListener('shopify:section:unload', (event) => {
      const root = event.target.matches?.('[data-sr-reels-section]') ? event.target : event.target.querySelector?.('[data-sr-reels-section]');
      if (!root) return;
      registry.controllers.get(root.dataset.sectionId)?.destroy();
      registry.controllers.delete(root.dataset.sectionId);
    });
    document.addEventListener('shopify:block:select', (event) => {
      const root = event.target.closest?.('[data-sr-reels-section]') || event.target.querySelector?.('[data-sr-reels-section]');
      const block = event.target.closest?.('[data-block-id]');
      const blockId = event.detail?.blockId || block?.dataset.blockId;
      if (root && blockId) registry.controllers.get(root.dataset.sectionId)?.handleBlockSelect(blockId);
    });
    document.addEventListener('shopify:block:deselect', (event) => {
      const root = event.target.closest?.('[data-sr-reels-section]') || event.target.querySelector?.('[data-sr-reels-section]');
      if (root) registry.controllers.get(root.dataset.sectionId)?.handleBlockDeselect();
    });
  }

  const boot = () => document.querySelectorAll('[data-sr-reels-section]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();